import { FloatingActionBar } from '@dev-spendesk/grapes';
import { type PayableRule } from '@spendesk/bookkeeping-core-types';
import { useState } from 'react';
import { useQueryClient } from 'react-query';

import { useTranslation } from 'common/hooks/useTranslation';
import { useNotifications } from 'modules/app/notifications';
import { useDeletePayableRulesMutation } from 'modules/bookkeep/settings/integrations/hooks/useDeletePayableRulesMutation';

import { PayableRuleDeleteConfirmationModal } from './components/PayableRuleDeleteConfirmationModal';
import { useUpdatePayableRulesStateFromTable } from './utils/useUpdatePayableRulesStateFromTable';
import { getPayableRulesQueryKey } from '../../hooks/useGetPayableRules';

type Props = {
  companyId: string;
  selectedPayableRules: Pick<PayableRule, 'id' | 'state'>[];
  resetSelectedPayableRules: () => void;
};

export const PayableRulesTableActions = ({
  companyId,
  selectedPayableRules,
  resetSelectedPayableRules,
}: Props) => {
  const { t } = useTranslation('global');
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const queryClient = useQueryClient();
  const { dangerNotif, successNotif } = useNotifications();
  const { mutateAsync: deletePayableRules } = useDeletePayableRulesMutation();
  const updatePayableRulesStateFromTable =
    useUpdatePayableRulesStateFromTable(companyId);

  if (selectedPayableRules.length === 0) {
    return null;
  }

  const selectedPayableRulesPublished = selectedPayableRules.filter(
    (rule) => rule.state === 'published',
  );
  const selectedPayableRulesDraft = selectedPayableRules.filter(
    (rule) => rule.state === 'draft',
  );

  return (
    <>
      <PayableRuleDeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        setIsOpen={setIsDeleteModalOpen}
        onDelete={async () => {
          try {
            await deletePayableRules({
              payableRuleIds: selectedPayableRules.map((rule) => rule.id),
            });
            queryClient.invalidateQueries(getPayableRulesQueryKey);
            resetSelectedPayableRules();
            successNotif(
              t(
                'bookkeep.integrations.settings.payableRules.rulesDeletedWithCount',
                { count: selectedPayableRules.length },
              ),
            );
          } catch {
            dangerNotif(t('misc.somethingWrong'));
          }
        }}
        numberOfRulesToBeDeleted={selectedPayableRules.length}
      />
      <FloatingActionBar
        actions={[
          ...(selectedPayableRulesDraft.length > 0
            ? [
                {
                  text: t(
                    'bookkeep.integrations.settings.payableRules.bulkActions.publishWithCount',
                    {
                      count: selectedPayableRulesDraft.length,
                    },
                  ),
                  iconName: 'circle-play' as const,
                  onClick: async () => {
                    await updatePayableRulesStateFromTable(
                      selectedPayableRulesDraft.map((rule) => rule.id),
                      'publish',
                    );
                  },
                },
              ]
            : []),
          ...(selectedPayableRulesPublished.length > 0
            ? [
                {
                  text: t(
                    'bookkeep.integrations.settings.payableRules.bulkActions.unpublishWithCount',
                    {
                      count: selectedPayableRulesPublished.length,
                    },
                  ),
                  iconName: 'circle-pause' as const,
                  onClick: async () => {
                    await updatePayableRulesStateFromTable(
                      selectedPayableRulesPublished.map((rule) => rule.id),
                      'unpublish',
                    );
                  },
                },
              ]
            : []),
          {
            text: t('misc.deleteWithCount', {
              count: selectedPayableRules.length,
            }),
            iconName: 'trash',
            onClick: () => setIsDeleteModalOpen(true),
          },
        ]}
      >
        {t('misc.nSelected', { count: selectedPayableRules.length })}
      </FloatingActionBar>
    </>
  );
};
